// Phone cameras produce photos of several MB while the api stores them at 2000px, so a
// photo is downscaled before it is uploaded: uploads stay quick on a site connection and
// rarely hit the size limit. Browsers decode with the exif orientation applied, so the
// photo keeps its rotation; when the browser cannot do the work the original is uploaded.
const MAX_DIMENSION = 2000
// what the api stores as it arrives; anything else is worth converting even when it grows
const KEPT_TYPES = ['image/jpeg', 'image/png']
const OUTPUT_TYPE = 'image/jpeg'
const OUTPUT_QUALITY = 0.85

const asUpload = (data, name) => ({ data, name, size: data.size })

const jpegName = name => `${name.replace(/\.[^.]+$/, '')}.jpg`

const toBlob = canvas =>
  new Promise(resolve => canvas.toBlob(resolve, OUTPUT_TYPE, OUTPUT_QUALITY))

const isSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.createImageBitmap === 'function' &&
  window.document != null

const ImageService = {
  async downscale(file) {
    const original = asUpload(file, file.name)
    if (!isSupported()) return original

    let bitmap
    try {
      bitmap = await window.createImageBitmap(file, {
        imageOrientation: 'from-image'
      })
    } catch (error) {
      // A photo the browser cannot decode is left for the api to reject
      return original
    }

    try {
      const canvas = window.document.createElement('canvas')
      if (typeof canvas.toBlob !== 'function') return original

      const scale = Math.min(
        1,
        MAX_DIMENSION / Math.max(bitmap.width, bitmap.height)
      )
      canvas.width = Math.round(bitmap.width * scale)
      canvas.height = Math.round(bitmap.height * scale)

      // A phone low on memory hands back no context for a canvas this size
      const context = canvas.getContext('2d')
      if (!context) return original

      // Jpeg has no transparency, so a transparent png would turn black without this
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)

      const blob = await toBlob(canvas)
      if (!blob) return original
      // Re-encoding can grow a photo that is already small, which is only worth keeping when
      // the api takes the original format anyway. A webp is smaller than any jpeg of it, and
      // sending it back would have the api refuse a photo the picker itself offered
      if (blob.size >= file.size && KEPT_TYPES.includes(file.type)) return original
      return asUpload(blob, jpegName(file.name))
    } catch (error) {
      // Running out of memory mid-resize must not lose the photo
      return original
    } finally {
      if (typeof bitmap.close === 'function') bitmap.close()
    }
  }
}

export default ImageService
